import React, { useEffect, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { IntlSellerAgreementDialog, isIntlAgreementError } from "@/components/IntlSellerAgreementDialog";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import PayoutProgressMeter from "@/components/PayoutProgressMeter";
import { BankIcon as Bank, CheckCircleIcon as CheckCircle, WarningIcon as Warning, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";

/**
 * Stripe Connect status banner — rendered on the breeder dashboard.
 *
 * Three states:
 *  - Not started → red "Set up payouts" CTA
 *  - In progress (account exists but payouts not enabled yet) → yellow nudge
 *  - Complete → green ✓ + small "Manage payouts" link to dashboard
 *
 * A breeder who never finishes Connect can't get paid for marketplace
 * sales, video tours, or upcoming-litter deposits — surfacing this on
 * day one prevents the day-30 "why didn't I get paid" support ticket.
 *
 * Backend contract:
 *   GET  /api/connect/account          → {onboarded, account|null}
 *   POST /api/connect/account {return_url} → {url, stripe_account_id}
 *   POST /api/connect/dashboard-link   → {url}
 */
export default function StripeConnectBanner() {
  const [status, setStatus] = useState(null); // null=loading, {}=loaded
  const [busy, setBusy] = useState(false);
  const [intlOpen, setIntlOpen] = useState(false);

  useEffect(() => {
    api.get("/connect/account", { silent: true })
      .then((r) => setStatus(r.data || {}))
      .catch(() => setStatus({}));
  }, []);

  if (status === null) return null;

  // The endpoint shape is { onboarded, account: { id, charges_enabled,
  // payouts_enabled, details_submitted, requirements_currently_due, ...} }.
  // `account` is null until the user actually starts onboarding.
  const acct = status.account || {};
  const hasAccount = !!acct.id;
  const payouts = !!acct.payouts_enabled;
  const submitted = !!acct.details_submitted;

  // All set — render a quiet success strip so the breeder feels confident
  // payouts work, but it doesn't dominate the dashboard.
  if (payouts) {
    return (
      <div
        className="mb-6 flex items-center justify-between gap-3 rounded-xl border border-[#BFE8C9] bg-[#EEFBEC] px-4 py-3"
        data-testid="stripe-connect-banner-ready"
      >
        <div className="flex items-center gap-2 text-[13px] text-[#0F7B3E]">
          <CheckCircle size={18} weight="fill" />
          <span><strong>Payouts ready.</strong> You'll receive funds from marketplace sales, video tours, and waitlist deposits straight to your bank account.</span>
        </div>
        <button
          onClick={async () => {
            setBusy(true);
            try {
              const { data } = await api.post("/connect/dashboard-link");
              if (data?.url) window.open(data.url, "_blank");
            } catch (e) {
              // Server returns a structured error with a Stripe-direct
              // fallback URL when the restricted API key can't open the
              // Express dashboard. Honor that by sending the seller to
              // dashboard.stripe.com directly instead of just toasting.
              const detail = e?.response?.data?.detail;
              if (detail && typeof detail === "object" && detail.stripe_direct_login_url) {
                toast.message(detail.message || "Opening Stripe directly", { duration: 7000 });
                window.open(detail.stripe_direct_login_url, "_blank");
              } else {
                toast.error(formatApiError(e));
              }
            } finally {
              setBusy(false);
            }
          }}
          disabled={busy}
          data-testid="stripe-connect-manage-link"
          className="text-[12px] font-bold text-ink hover:text-crimson underline whitespace-nowrap"
        >
          Manage payouts ↗
        </button>
      </div>
    );
  }

  const onStart = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/connect/account", {
        return_url: `${webOrigin()}/connect/return?flow=breeder`,
      });
      if (data?.url) window.location.href = data.url;
      else throw new Error("Stripe onboarding link unavailable.");
    } catch (e) {
      if (isIntlAgreementError(e)) {
        setIntlOpen(true);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const due = acct.requirements_currently_due || [];
  const dueLabels = acct.requirements_currently_due_labels || due;

  return (
    <div
      className={`mb-6 rounded-xl border-2 p-4 sm:p-5 flex items-start gap-3 ${
        hasAccount && submitted
          ? "border-[#E8D9A6] bg-[#FBF7EC]"
          : "border-crimson/40 bg-[#FFF0F0]"
      }`}
      data-testid={hasAccount && submitted ? "stripe-connect-banner-progress" : "stripe-connect-banner-missing"}
    >
      <IntlSellerAgreementDialog open={intlOpen} onOpenChange={setIntlOpen} onAccepted={onStart} />
      <span className={`p-2 rounded-full ${hasAccount && submitted ? "bg-[#FBF1D2]" : "bg-[#FDEEEE]"}`}>
        {hasAccount && submitted ? (
          <Warning size={20} weight="fill" className="text-[#8A6A00]" />
        ) : (
          <Bank size={20} weight="duotone" className="text-crimson" />
        )}
      </span>
      <div className="flex-1 min-w-0">
        <h3 className="font-spectral text-[17px] sm:text-lg leading-tight">
          {hasAccount && submitted
            ? "Finish payouts setup"
            : "You can't get paid yet — finish payouts setup"}
        </h3>
        <p className="text-[12.5px] sm:text-[13px] text-[#3F3F3F] mt-1 leading-snug">
          {hasAccount && submitted
            ? "Stripe is reviewing the rest of your info. Most accounts clear in 24 hours. Until they do, marketplace sales, video tours, and waitlist deposits will sit in escrow."
            : "Marketplace sales, video tours, and waitlist deposits all flow through Stripe Connect — a free 3-minute setup that links your bank account so we can pay you directly. You can't receive a single payout until this is done."}
        </p>
        {hasAccount && <PayoutProgressMeter progress={acct.setup_progress} />}
        {dueLabels.length > 0 && (
          <div className="mt-2.5 rounded-lg bg-white/60 border border-[#E8D9A6] px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#8A4A00] mb-1">Still needed</p>
            <ul className="text-[12.5px] text-ink space-y-0.5 leading-snug list-disc pl-4">
              {dueLabels.slice(0, 4).map((label, i) => (
                <li key={`${label}-${i}`}>{label}</li>
              ))}
              {dueLabels.length > 4 && (
                <li className="text-[#6B6B6B] italic">…and {dueLabels.length - 4} more</li>
              )}
            </ul>
          </div>
        )}
        <Button
          onClick={onStart}
          disabled={busy}
          data-testid="stripe-connect-start-btn"
          className="mt-3 bg-ink hover:bg-crimson text-white rounded-full h-10 px-5"
        >
          {hasAccount ? "Continue setup" : "Set up payouts"} <ArrowRight size={13} weight="bold" className="ml-1" />
        </Button>
        {!submitted && (
          <p className="mt-2 text-[11px] text-[#6B6B6B] leading-snug" data-testid="stripe-preflight-hint">
            Takes about 5 minutes. Have handy: a photo ID, your bank routing
            &amp; account numbers, and your SSN (or EIN for a business).
          </p>
        )}
        {hasAccount && (
          <p className="mt-2 text-[11px] text-[#6B6B6B] leading-snug">
            Already finished on Stripe but still see this?{" "}
            <button
              type="button"
              onClick={async () => {
                try {
                  const r = await import("@/lib/api").then((m) => m.default.post("/connect/account/login-link"));
                  if (r?.data?.url) {
                    window.location.href = r.data.url;
                  }
                } catch (err) {
                  // Fall back to the un-authenticated dashboard URL.
                  window.open("https://dashboard.stripe.com", "_blank", "noreferrer");
                }
              }}
              data-testid="stripe-direct-dashboard-link"
              className="underline font-semibold text-ink hover:text-crimson"
            >
              Sign in to Stripe directly ↗
            </button>{" "}
            and finish there. Stripe will email you when payouts go live and
            our banner will flip green within 60 seconds.
          </p>
        )}
      </div>
    </div>
  );
}
